import api from './api';
import type { ServerSetting } from './adminServerSettings';

export type UnitState = 'Starting' | 'Idle' | 'Busy' | 'Draining';

export type UnitExitReason = 'IdleTimeout' | 'MaxJobs' | 'MaxAge' | 'Drain' | 'LowDisk' | 'HostStopped' | 'Lost';

/** A unit's current job; the job's fields are null when its row no longer exists. */
export interface UnitJob {
  jobId: string;
  jobType: string | null;
  projectId: string | null;
  projectName: string | null;
  userEmail: string | null;
}

export interface WorkerUnit {
  unitId: string;
  groupName: string;
  region: string;
  imageTag: string;
  cpuCores: number;
  memoryGb: number;
  state: UnitState;
  currentJob: UnitJob | null;
  jobsDone: number;
  startedAt: string;
  lastHeartbeatAt: string;
  memoryUsedBytes: number | null;
  cpuUsedCores: number | null;
  freeDiskBytes: number | null;
  /** Over the unit's life so far; null until its first sample. */
  usage: UnitUsage | null;
  drain: boolean;
  keepWarm: boolean;
}

/** Usage over a unit's life, or over the lives of an image's units, from samples taken every 30 s and at status changes. */
export interface UnitUsage {
  samples: number;
  /** The mean of the samples, each the cores used on average since the one before. */
  cpuAverageCores: number;
  /** The highest interval average: the most cores a sample used on average since the one before. */
  cpuPeakCores: number;
  memoryAverageBytes: number;
  memoryPeakBytes: number;
  /** The least free disk of the samples; null while none could read it. */
  freeDiskMinBytes: number | null;
}

/** What the units of one image and size that ended in the 7 days before the reading used. */
export interface ImageSizing {
  imageTag: string;
  cpuCores: number;
  memoryGb: number;
  units: number;
  /** Over all their samples: means of every sample, the highest peaks, the least free disk. */
  usage: UnitUsage;
}

export interface UnitEndings {
  reason: UnitExitReason;
  count: number;
}

export type GroupLimit = 'Disk' | 'Cpu' | 'Memory';

/** What the unit manager wrote at the end of its last round. */
export interface UnitManagerStatus {
  lastRoundAt: string;
  managerHost: string;
  /** The Azure region the manager creates units in. */
  region: string;
  /** Until when failed starts pause the creation of units; null while they do not. */
  backOffUntil: string | null;
  failedStartsInARow: number;
  /** The image tag whose units failed to start; null while none failed. */
  failedStartsImageTag: string | null;
  /** The most units of the policy's size that fit one group, 0 when not one does; null while the manager could not read the policy. */
  unitsPerGroupCap: number | null;
  unitsPerGroupBoundBy: GroupLimit | null;
  /** Azure's last refusal of a group for lack of room in the region; null since Azure accepted one. */
  capacityRefusal: CapacityRefusal | null;
}

export interface CapacityRefusal {
  /** When Azure refused last. */
  at: string;
  /** The first line of Azure's message. */
  message: string;
  /** The quota Azure named, such as StandardCores; null when the region had no capacity. */
  quota: { name: string; limit: number; usage: number; requested: number } | null;
}

export type ImageSource = 'Deploy' | 'ManualDeploy' | 'BuildWorkflow' | 'Admin' | 'Manager';

export interface KnownImage {
  tag: string;
  /** The first writer of the tag, or the unit manager when it saw the tag before any writer recorded it. */
  recordedBy: ImageSource;
  firstRecordedAt: string;
  /** The last time the tag was stored as the image tag; null while only the unit manager saw it. */
  lastWrittenAt: string | null;
  /** The last round in which a unit of the tag finished a job; null until one did. */
  lastWorkedAt: string | null;
  unitsThatRanIt: number;
}

export interface LiveUnits {
  units: WorkerUnit[];
  /** The units that ended in the 24 hours before the reading, by exit reason. */
  endedLastDay: UnitEndings[];
}

/** One part of the overview: its value, or null and why the server could not read it. */
export interface OverviewPart<T> {
  value: T | null;
  error: string | null;
}

export interface UnitsOverview {
  /** When the server read the parts; ages are measured from it. */
  asOf: string;
  live: OverviewPart<LiveUnits>;
  /** A value of null without an error: the unit manager never wrote its status. */
  manager: OverviewPart<UnitManagerStatus>;
  /** The image tags recorded last, the newest first. */
  images: OverviewPart<KnownImage[]>;
  /** Per image and size, the one whose last unit ended last first. */
  sizing: OverviewPart<ImageSizing[]>;
}

export interface JobInterruption {
  id: string;
  interruptedAt: string;
  jobId: string;
  projectId: string;
  projectName: string | null;
  userId: string;
  userEmail: string | null;
  workerId: string;
  reason: 'PlatformStop' | 'LastAttempt';
  attemptNumber: number;
  minutesLost: number;
  creditsRefunded: number;
  imagesRefunded: number;
  emergencyCommitSaved: boolean;
}

export interface MissingImagePackage {
  packageId: string;
  packageVersion: string;
  kind: 'NewPackage' | 'NewVersion';
  bakedVersions: string[];
  engineVersion: string;
  imageTag: string;
  firstSeenAt: string;
  lastSeenAt: string;
  /** How many worker processes found the package; each reports a package once. */
  timesSeen: number;
}

/** One sample of a unit, or, for a unit with more than 600, consecutive samples: their mean CPU, highest memory, least free disk. */
export interface UsagePoint {
  at: string;
  cpuCores: number;
  memoryBytes: number;
  freeDiskBytes: number | null;
}

export interface UnitSamples {
  unitId: string;
  /** The unit's samples still kept (7 days), which the points stand for. */
  samples: number;
  points: UsagePoint[];
}

export interface EndedUnit {
  unitId: string;
  startedAt: string;
  endedAt: string;
  jobsDone: number;
  exitReason: UnitExitReason;
}

export interface CostGroup {
  group: string;
  cost: number;
  /** The Azure services whose costs the group sums. */
  azureServices: string[];
}

/** One period of the subscription's actual costs, from its first to its last day (UTC), as Azure answered at `readAt`. */
export interface AzureCosts {
  firstDay: string;
  lastDay: string;
  readAt: string;
  currency: string;
  total: number;
  /** Every group of the page, in its order, Other last. */
  groups: CostGroup[];
}

/** The units' lives in the `days` before the reading; a life counts from its worker's start, so image pulls are left out. */
export interface UnitHours {
  days: number;
  hours: number;
  units: number;
  jobsDone: number;
}

export interface CostPrices {
  currency: string;
  pricesAsOf: string;
  unitCorePerHour: number;
  unitGbPerHour: number;
  managerAppPerMonth: number;
}

export interface UnitCosts {
  asOf: string;
  lastMonth: OverviewPart<AzureCosts>;
  monthToDate: OverviewPart<AzureCosts>;
  unitHours: OverviewPart<UnitHours>;
  prices: CostPrices;
}

export async function getUnitCosts(): Promise<UnitCosts> {
  const response = await api.get<UnitCosts>('admin/units/costs');
  return response.data;
}

export async function getUnitSamples(unitId: string): Promise<UnitSamples> {
  const response = await api.get<UnitSamples>(`admin/units/${encodeURIComponent(unitId)}/samples`);
  return response.data;
}

/** The units of the image and size that ended in the last 7 days, the last ended first. */
export async function getEndedUnits(imageTag: string, cpuCores: number, memoryGb: number): Promise<EndedUnit[]> {
  const response = await api.get<EndedUnit[]>('admin/units/ended', { params: { imageTag, cpuCores, memoryGb } });
  return response.data;
}

export async function getUnitsOverview(): Promise<UnitsOverview> {
  const response = await api.get<UnitsOverview>('admin/units');
  return response.data;
}

/** Stores the tag as the image of new units and records it among the known images; answers the stored setting. */
export async function chooseImageTag(tag: string): Promise<ServerSetting> {
  const response = await api.put<ServerSetting>('admin/units/image-tag', { tag });
  return response.data;
}

export async function getJobInterruptions(): Promise<JobInterruption[]> {
  const response = await api.get<JobInterruption[]>('admin/units/interruptions');
  return response.data;
}

export async function getMissingImagePackages(): Promise<MissingImagePackage[]> {
  const response = await api.get<MissingImagePackage[]>('admin/units/missing-packages');
  return response.data;
}
